import { useMemo, useCallback } from 'react';
import {
  useFilterPresetsStore,
  useAllPresets,
} from '../store/filterPresetsStore';
import {
  useTabScopedRequestsFilters,
  hasActiveRequestsFilters,
  matchesExtensionFilter,
  matchesMimeTypeFilters,
  type RequestHttpMethod,
} from '../store/tab/requestsTabScopedFilterStore';
import { createTqlFilter, type HttpRequestContext } from '../utils/tql';
import {
  formatRequestToText,
  formatResponseToText,
} from '../utils/httpFormatter';

export function toTqlContext(request: {
  id: string;
  method: string;
  url: string;
  host?: string;
  headers?: Record<string, string | string[]>;
  body?: string;
  status?: number;
  responseHeaders?: Record<string, string | string[]>;
  responseBody?: string;
  length?: number;
  roundtrip?: number;
  comment?: string;
  tags?: string[];
  highlight?: string | null;
  timestamp?: Date | string | number;
}): HttpRequestContext {
  let parsedHost = request.host || '';
  let parsedPath = '';
  let parsedQuery = '';
  let parsedExt = '';
  let parsedPort = 443;
  let responseMime = '';

  try {
    const url = new URL(request.url);

    parsedHost = url.hostname;
    parsedPath = url.pathname;
    parsedQuery = url.search.substring(1);
    parsedPort = url.port
      ? parseInt(url.port, 10)
      : url.protocol === 'https:'
        ? 443
        : 80;

    const lastDot = parsedPath.lastIndexOf('.');

    if (lastDot > 0 && lastDot > parsedPath.lastIndexOf('/')) {
      parsedExt = parsedPath.substring(lastDot);
    }
  } catch {
    parsedPath = request.url;
  }

  if (request.responseHeaders) {
    const contentType =
      request.responseHeaders['content-type'] ||
      request.responseHeaders['Content-Type'];

    if (contentType) {
      responseMime = Array.isArray(contentType) ? contentType[0] : contentType;
    }
  }

  // Generate raw request
  const rawRequest = formatRequestToText({
    method: request.method,
    url: request.url,
    headers: request.headers || {},
    body: request.body,
  });

  // Generate raw response if response data exists
  let rawResponse: string | undefined;

  if (
    request.status !== undefined &&
    request.status > 0 &&
    request.responseHeaders
  ) {
    rawResponse = formatResponseToText({
      statusCode: request.status,
      headers: request.responseHeaders,
      body: request.responseBody,
    });
  }

  return {
    id: request.id,
    method: request.method,
    url: request.url,
    host: parsedHost,
    path: parsedPath,
    query: parsedQuery,
    ext: parsedExt,
    port: parsedPort,
    headers: request.headers || {},
    body: request.body,
    rawRequest,
    status: request.status,
    responseHeaders: request.responseHeaders || {},
    responseMime,
    responseBody: request.responseBody,
    rawResponse,
    length: request.length,
    roundtrip: request.roundtrip,
    comment: request.comment,
    tags: request.tags || [],
    highlight: request.highlight || null,
    timestamp: request.timestamp
      ? typeof request.timestamp === 'number'
        ? request.timestamp
        : new Date(request.timestamp).getTime()
      : Date.now(),
  };
}

export function useTqlFilter() {
  const {
    searchTerm,
    methodFilter,
    statusFilter,
    mimeTypeFilter,
    selectedExtensions,
    extensionFilterMode,
    filterMode,
    tqlQuery: storedTqlQuery,
    setFilterMode,
    setTqlQuery,
  } = useTabScopedRequestsFilters();

  const { activeQuery, resolvePreset } = useFilterPresetsStore();
  const presets = useAllPresets();

  const tqlQuery = activeQuery || storedTqlQuery;

  const tqlFilter = useMemo(() => {
    if (filterMode !== 'advanced') return null;
    if (!tqlQuery.trim()) return null;

    return createTqlFilter(tqlQuery, resolvePreset);
  }, [tqlQuery, filterMode, resolvePreset]);

  const matchesSimpleFilters = useCallback(
    (context: HttpRequestContext): boolean => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
          context.url.toLowerCase().includes(term) ||
          (context.host?.toLowerCase().includes(term) ?? false) ||
          (context.path?.toLowerCase().includes(term) ?? false) ||
          context.method.toLowerCase().includes(term) ||
          (context.comment?.toLowerCase().includes(term) ?? false);

        if (!matchesSearch) return false;
      }

      if (
        methodFilter.length > 0 &&
        !methodFilter.includes(
          context.method.toUpperCase() as RequestHttpMethod,
        )
      ) {
        return false;
      }

      if (statusFilter.length > 0 && context.status !== undefined) {
        const requestGroup = Math.floor(context.status / 100);
        const matchesStatus = statusFilter.some((statusBucket) => {
          const statusGroup = parseInt(statusBucket[0], 10);

          return statusGroup === requestGroup;
        });

        if (!matchesStatus) {
          return false;
        }
      }

      if (mimeTypeFilter.length > 0 && context.responseHeaders) {
        if (!matchesMimeTypeFilters(context.responseHeaders, mimeTypeFilter)) {
          return false;
        }
      }

      if (selectedExtensions.length > 0) {
        if (
          !matchesExtensionFilter(
            context.url,
            selectedExtensions,
            extensionFilterMode,
          )
        ) {
          return false;
        }
      }

      return true;
    },
    [
      searchTerm,
      methodFilter,
      statusFilter,
      mimeTypeFilter,
      selectedExtensions,
      extensionFilterMode,
    ],
  );

  const matchesFilters = useCallback(
    (request: HttpRequestContext): boolean => {
      if (filterMode === 'simple') {
        return matchesSimpleFilters(request);
      }

      if (!tqlFilter) {
        return true;
      }

      return tqlFilter(request);
    },
    [matchesSimpleFilters, filterMode, tqlFilter],
  );

  const hasActiveFilters = useMemo(() => {
    return hasActiveRequestsFilters({
      searchTerm,
      methodFilter,
      statusFilter,
      mimeTypeFilter,
      selectedExtensions,
      tqlQuery,
      filterMode,
    });
  }, [
    searchTerm,
    methodFilter,
    statusFilter,
    mimeTypeFilter,
    selectedExtensions,
    filterMode,
    tqlQuery,
  ]);

  return {
    searchTerm,
    methodFilter,
    statusFilter,
    mimeTypeFilter,
    selectedExtensions,
    tqlQuery,
    filterMode,
    activeQuery,
    presets,
    hasActiveFilters,
    setTqlQuery,
    setFilterMode,
    matchesFilters,
    matchesSimpleFilters,
    tqlFilter,
    toTqlContext,
  };
}
